import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  Users,
} from "@/components/Icons.tsx";
import React from "react";
import { Organisation } from "@/types.ts";
import OrgSwitcher from "./OrgSwitcher";
import { useNavigate } from "react-router-dom";
import { SideNavUserButton } from "@/components/SideNavUserButton.tsx";

interface SideNavProps {
  currentOrg: Organisation;
  organisations: Organisation[];
  onSwitchOrg: (org: Organisation) => void;
  onOrgCreated: (org: Organisation) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  isActive: (path: string) => boolean;
  tpUser: {
    name: string;
    email: string;
  };
  onOpenCreateOrg: () => void;
}

export const SideNav: React.FC<SideNavProps> = ({
  currentOrg,
  organisations,
  onSwitchOrg,
  onOrgCreated,
  mobileMenuOpen,
  setMobileMenuOpen,
  isActive,
  tpUser,
  onOpenCreateOrg,
}) => {
  const navigate = useNavigate();
  return (
    <aside
      className={`fixed md:relative z-drawer w-72 h-full bg-slate-900 border-r border-slate-800 flex flex-col transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
    >
      <div className="mb-8 p-3 border-b border-slate-800">
        <OrgSwitcher
          currentOrg={currentOrg}
          allOrgs={organisations}
          onChange={onSwitchOrg}
          onOrgCreated={onOrgCreated}
          onOpenCreateOrg={onOpenCreateOrg}
        />
      </div>

      <div className="p-5 flex-1">
        <nav className="space-y-1">
          <NavButton
            active={isActive("/dashboard")}
            onClick={() => {
              navigate(`/${currentOrg.slug}/dashboard`);
              setMobileMenuOpen(false);
            }}
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            testId="nav-dashboard"
          />
          <NavButton
            active={isActive("/workspace")}
            onClick={() => {
              navigate(`/${currentOrg.slug}/workspace`);
              setMobileMenuOpen(false);
            }}
            icon={<Users size={20} />}
            label="Workspace"
            testId="nav-workspace"
          />
          <NavButton
            active={isActive("/settings")}
            onClick={() => {
              navigate(`/${currentOrg.slug}/settings`);
              setMobileMenuOpen(false);
            }}
            icon={<Settings size={20} />}
            label="Settings"
            testId="nav-settings"
          />
        </nav>
      </div>

      <div className="p-5 border-t border-slate-800">
        <a
          href="https://tally.so/r/xXaX7r"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-all duration-200"
        >
          <MessageSquare size={20} />
          Feedback
        </a>
      </div>
      <SideNavUserButton tpUser={tpUser} />
    </aside>
  );
};

// Helper Component for Sidebar Buttons
const NavButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  testId?: string;
}> = ({ active, onClick, icon, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
      active
        ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20"
        : "text-slate-400 hover:bg-slate-800 hover:text-white"
    }`}
  >
    {icon}
    {label}
  </button>
);
